import { CodeError } from '@iotize/common/error';
import { TapRequestFrame } from '@iotize/tap/client/api';
import {
  TapRequestHelper,
  tapResponseStatusToString,
} from '@iotize/tap/client/impl';

import { ServiceCallType } from './definitions';
import { TapResponse } from './response-impl';
import { ServiceCallRunner, serviceCallToString } from './service-call-runner';

export class TapError extends CodeError<TapError.Code> {
  constructor(
    code: TapError.Code,
    message: string,
    public cause?: Error,
  ) {
    super(message, code);
  }

  public static cannotStartScram(err: Error): TapError {
    return new TapError(
      TapError.Code.CannotStartScram,
      `Cannot start encrypted session, initialization failed. Cause: ${err.message}`,
      err,
    );
  }

  static notImplementedYet(msg: string): TapError {
    throw new TapError(TapError.Code.NotImplemented, msg);
  }

  static initializationVectorNotSupported(err: Error) {
    return new TapError(
      TapError.Code.InitializationVectorNotSupported,
      `Initialization vector for SCRAM is not implemented with your current firmware version. You cannot use this feature.`,
      err,
    );
  }

  static executeRequestError<BodyType, ReturnType>(
    request: ServiceCallType<BodyType, ReturnType>,
    err: Error,
  ) {
    return new TapError(
      TapError.Code.ExecuteRequestError,
      `Tap request ${TapRequestFrame.MethodType[request.method]} ${
        request.pathAlias || request.path
      } failed. ${err.message}`,
      err,
    );
  }

  static reponseStatusError(
    response: TapResponse<any>,
    call?: ServiceCallType<any, any>,
  ) {
    return new TapResponseStatusError(response, call);
  }

  static invalidServiceCallConfiguration(msg: string) {
    return new TapError(TapError.Code.InvalidServiceCallConfiguration, msg);
  }

  static appendChunkError(
    cause: Error,
    chunk: Uint8Array,
    progress: ServiceCallRunner.AppendProgress,
  ) {
    return new AppendChunkError(cause, chunk, progress);
  }

  static nfcPairingError(msg: string, cause?: Error) {
    return new TapError(
      TapError.Code.NfcPairingError,
      `NFC pairing failed. ${msg}`,
      cause,
    );
  }
}

export namespace TapError {
  export enum Code {
    CannotStartScram = 'TapErrorCannotStartScram',
    NotImplemented = 'TapErrorNotImplemented',
    InitializationVectorNotSupported = 'TapErrorInitializationVectorNotSupported',
    AppendChunkError = 'AppendChunkError',
    ResponseStatusError = 'TapErrorTapResponseStatusError',
    InvalidServiceCallConfiguration = 'TapErrorInvalidServiceCallConfiguration',
    ExecuteRequestError = 'TapErrorTapExecuteRequestError',
    NfcPairingError = 'TapErrorNfcPairingError',
    ScramNotStartedYet = 'TapErrorScramNotStartedYet',
    InvalidScramKey = 'TapErrorInvalidScramKey',
  }
}

export class TapResponseStatusError extends TapError {
  constructor(
    public response: TapResponse<any>,
    call?: ServiceCallType<any, any>,
  ) {
    super(
      TapError.Code.ResponseStatusError,
      `Tap request ${
        call
          ? `${serviceCallToString(call)} `
          : response.request
            ? TapRequestHelper.toString(response.request) + ' '
            : ''
      }failed. ${tapResponseStatusToString(
        response.status,
      )} (status code 0x${response.status.toString(16)})`,
    );
  }
}

export class AppendChunkError extends TapError {
  constructor(
    cause: Error,
    public chunk: Uint8Array,
    public progress: ServiceCallRunner.AppendProgress,
  ) {
    super(
      TapError.Code.AppendChunkError,
      `Appending data chunk n°${progress.loaded}/${progress.total} failed with error: ${cause.message}`,
      cause,
    );
  }
}
